import { Button, Checkbox, Icon } from '@smartgift/frontend-toolkit';
import isEqual from 'lodash/isEqual';
import merge from 'lodash/merge';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAddTagModal, useDeleteModalTemplate } from '@hero/hooks';
import { Table } from '@hero/molecules';
import { EditTagContactsModal } from '@hero/organisms';
import { useGetTagList, useRemoveTag } from '@hero/services/contact-service/hooks';

import { CustomSelectionHead } from './CustomSelectionHead';
import * as S from './style';

function SelectCell({ row, table }) {
  const { disabledRows } = table.getState();

  return (
    <Checkbox
      {...{
        checked: !!disabledRows?.[row.id] || row.getIsSelected(),
        disabled: !!disabledRows?.[row.id],
        indeterminate: row.getIsSomeSelected(),
        onChange: row.getToggleSelectedHandler()
      }}
    />
  );
}

SelectCell.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.string,
    getIsSelected: PropTypes.func,
    getIsSomeSelected: PropTypes.func,
    getToggleSelectedHandler: PropTypes.func
  }),
  table: PropTypes.shape({
    getState: PropTypes.func
  })
};

function SelectHeader({ table }) {
  const { disabledRows, rowSelection: selectedRows } = table.getState();

  const dRows = table.getRowModel().rows.filter(row => disabledRows[row.id]);
  const rowLength = table.getRowModel().rows.length;
  const disabled = rowLength === dRows.length;
  const checkedLength = table
    .getRowModel()
    .rows.filter(row => !disabledRows[row.id] && selectedRows[row.id]).length;
  const checked = checkedLength === rowLength - dRows.length;
  const indeterminate = checkedLength > 0 && checkedLength < rowLength - dRows.length;

  return (
    <Checkbox
      {...{
        checked: disabled || checked,
        disabled,
        indeterminate,
        onChange: e => {
          table.toggleAllPageRowsSelected(e.target.checked);
        }
      }}
    />
  );
}

SelectHeader.propTypes = {
  table: PropTypes.shape({
    getState: PropTypes.func,
    getRowModel: PropTypes.func,
    toggleAllPageRowsSelected: PropTypes.func
  })
};

function TagTable({
  selectable,
  defaultPageSize = 10,
  selectedRows,
  onChangeSelectedRows,
  defaultParams = { enabled: true },
  disabledRows = {},
  isEditContactsModalOpen = false,
  defaultSelectedTags,
  disabledColumns,
  isDeleteActionShown = true,
  ...props
}) {
  const { t } = useTranslation();
  const { mutateAsync: removeTag } = useRemoveTag();
  const { addTag } = useAddTagModal();
  const { showDeleteModal } = useDeleteModalTemplate({
    confirmText: 'DELETE TAG',
    title: t('areYouSureYouWantDelete', { type: t('tag').toLowerCase() }),
    message: t('deleteTagConfirmMessage')
  });

  const [globalFilters, setGlobalFilters] = useState({});
  const [selectedTag, setSelectedTag] = useState(null);
  const [openEditTabContactsModal, setOpenEditTabContactsModal] = useState(false);
  const [rowSelection, setRowSelection] = useState(selectedRows || {});
  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: defaultPageSize
  });

  const onRequestEditTagContactModalClose = () => {
    setOpenEditTabContactsModal(false);
    setSelectedTag(null);
  };

  const rowSelectionArray = useMemo(() => {
    return Object.keys(rowSelection || {});
  }, [rowSelection]);

  const tagListParams = useMemo(() => {
    return merge(
      {},
      {
        filters: {
          searchTerm: globalFilters.searchTerm
        },
        pagination: {
          page: pageIndex + 1,
          limit: pageSize
        },
        options: {
          includeContacts: true,
          includeAutomations: true
        }
      },
      defaultParams
    );
  }, [pageIndex, pageSize, globalFilters, defaultParams]);

  const { data: tags, isLoading } = useGetTagList(tagListParams, {
    enabled: defaultParams?.enabled
  });

  function removeCell({ row }) {
    return (
      <S.RemoveContent>
        <S.RemoveButton
          variant="link"
          color="secondary"
          onClick={() => {
            showDeleteModal({
              onSubmit: () => {
                removeTag(row.id);
              },
              notification: row.original?.automations?.length
                ? {
                    message: t('activeAutomationTagInfo', {
                      tagName: row.original.name
                    }),
                    status: 'warning'
                  }
                : undefined
            });
          }}
        >
          <Icon name="delete" />
        </S.RemoveButton>
      </S.RemoveContent>
    );
  }

  const columns = React.useMemo(
    () => [
      ...(selectable
        ? [
            {
              id: 'select',
              maxSize: 30,
              header: SelectHeader,
              cell: SelectCell
            }
          ]
        : []),
      { header: t('tag'), accessorKey: 'name' },
      { header: t('contacts'), accessorKey: 'contactsCount' },
      {
        header: t('viewList'),
        accessorKey: '',
        cell: ({ row }) => (
          <Button
            variant="link"
            color="neutral"
            onClick={() => {
              setSelectedTag([row.original]);
              setOpenEditTabContactsModal(true);
            }}
          >
            {t('showEditContacts')}
          </Button>
        )
      },
      ...(isDeleteActionShown
        ? [
            {
              id: 'remove',
              accessorKey: '',
              cell: removeCell
            }
          ]
        : [])
    ],
    []
  );

  React.useEffect(() => {
    if (selectedRows && !isEqual(selectedRows, rowSelection)) {
      setRowSelection(selectedRows);
    }
  }, [selectedRows]);

  React.useEffect(() => {
    if (defaultSelectedTags) {
      setSelectedTag(defaultSelectedTags);
    }
  }, [defaultSelectedTags]);

  React.useEffect(() => {
    if (onChangeSelectedRows && !isEqual(selectedRows, rowSelection))
      onChangeSelectedRows(rowSelection);
  }, [rowSelection]);

  return (
    <>
      <Table
        columns={columns}
        data={tags?.docs || []}
        globalFilters={globalFilters}
        state={{
          disabledRows
        }}
        pagination={{
          pageIndex,
          pageSize,
          total: tags?.pagination?.total
        }}
        rowHeight={52}
        pageCount={tags?.pagination?.pages}
        manualPagination
        loading={isLoading && defaultParams?.enabled}
        setRowSelection={setRowSelection}
        rowSelection={rowSelection}
        setGlobalFilters={setGlobalFilters}
        setPagination={setPagination}
        enabled={{ filter: false, header: true }}
        filterPlaceholder={t('filterByType', { type: t('tags') })}
        translations={{
          selectedRowCountText: t('selectedRowCount', {
            selectedRowCount: rowSelectionArray.length,
            totalContactCount: tags?.pagination?.total
          }),
          filteredWithText: t('filteredWithText', {
            filteredContactCount: tags?.pagination?.total || '0'
          })
        }}
        enableRowSelection={row => {
          return !disabledRows?.[row.id];
        }}
        emptyTableContent={{
          feature: 'tags',
          icon: 'tag',
          createNew: addTag
        }}
        {...props}
        components={{
          ...props?.components,
          SelectionHead: CustomSelectionHead
        }}
      />
      {selectedTag && (
        <EditTagContactsModal
          selectedTags={selectedTag}
          openModal={isEditContactsModalOpen || openEditTabContactsModal}
          onRequestClose={onRequestEditTagContactModalClose}
          disabledColumns={disabledColumns}
        />
      )}
    </>
  );
}

TagTable.propTypes = {
  selectable: PropTypes.bool,
  defaultPageSize: PropTypes.number,
  selectedRows: PropTypes.array,
  onChangeSelectedRows: PropTypes.func,
  defaultParams: PropTypes.shape({
    enabled: PropTypes.bool
  }),
  disabledRows: PropTypes.array,
  isEditContactsModalOpen: PropTypes.bool,
  defaultSelectedTags: PropTypes.array,
  disabledColumns: PropTypes.array,
  isDeleteActionShown: PropTypes.bool,
  props: PropTypes.shape({
    components: PropTypes.array
  })
};

export { TagTable };
